// ─── Archel metadata ─────────────────────────────────────────────────────────

export interface ArchelMeta {
  id: number;
  name: string;
  color: string;
  rgb: [number, number, number];
}

/** Well-known colors for the first few archel ids. */
const ARCHEL_PALETTE: Record<
  number,
  { color: string; rgb: [number, number, number] }
> = {
  0: { color: '#BDBDBD', rgb: [189, 189, 189] },
  1: { color: '#1565C0', rgb: [21, 101, 192] },
  2: { color: '#2E7D32', rgb: [46, 125, 50] },
  3: { color: '#BF360C', rgb: [191, 54, 12] },
  4: { color: '#6A1B9A', rgb: [106, 27, 154] },
  5: { color: '#F57F17', rgb: [245, 127, 23] },
  6: { color: '#6D4C41', rgb: [109, 76, 65] },
  7: { color: '#00838F', rgb: [0, 131, 143] },
  8: { color: '#AD1457', rgb: [173, 20, 87] },
  9: { color: '#558B2F', rgb: [85, 139, 47] },
};

/** Generate a deterministic color for any archel id not in the palette. */
function generateColor(id: number): {
  color: string;
  rgb: [number, number, number];
} {
  // Distribute hues evenly using the golden ratio to avoid similar neighbors
  const hue = Math.round((id * 137.508) % 360);
  const s = 65;
  const l = 40;
  // Convert HSL → RGB
  const a = (s * Math.min(l, 100 - l)) / 100;
  const f = (n: number) => {
    const k = (n + hue / 30) % 12;
    return Math.round(
      ((l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1)) / 100) * 255,
    );
  };
  const rgb: [number, number, number] = [f(0), f(8), f(4)];
  const color = `#${rgb.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
  return { color, rgb };
}

/** Parse a CSS hex color (#rgb, #rrggbb) to an RGB tuple, or return null. */
function hexToRgb(hex: string): [number, number, number] | null {
  const clean = hex.trim().replace(/^#/, '');
  if (clean.length === 3) {
    const r = parseInt(clean[0] + clean[0], 16);
    const g = parseInt(clean[1] + clean[1], 16);
    const b = parseInt(clean[2] + clean[2], 16);
    return isNaN(r) || isNaN(g) || isNaN(b) ? null : [r, g, b];
  }
  if (clean.length === 6) {
    const r = parseInt(clean.slice(0, 2), 16);
    const g = parseInt(clean.slice(2, 4), 16);
    const b = parseInt(clean.slice(4, 6), 16);
    return isNaN(r) || isNaN(g) || isNaN(b) ? null : [r, g, b];
  }
  return null;
}

/**
 * Builds runtime ArchelMeta[] from the model's `iniParameters.archels` list.
 *
 * Display name priority:
 *   1. `analogueModelComputeSettingArchelMap.identifier` — the SMDA name the
 *      user mapped this archel to (most meaningful)
 *   2. `name` — the archel's own name from the simulation config
 *   3. `Archel ${id}` — fallback
 *
 * Color priority when an archel is mapped:
 *   1. `colorHtml` from the matched GeologicalStandardDto (SMDA-defined color)
 *   2. Well-known palette for the archel id
 *   3. Deterministically generated color
 */
export function buildArchelMeta(
  archels: Array<{
    value?: string | null;
    name?: string | null;
    analogueModelComputeSettingArchelMap?: {
      identifier?: string | null;
      smdaGeologyStandardId?: string;
      equinorCode?: number;
    } | null;
  }>,
  geologyStandards: Array<{
    geologicalStandardId: string;
    equinorCode: number;
    colorHtml?: string | null;
  }> = [],
): ArchelMeta[] {
  const standardsById = new Map(
    geologyStandards.map((s) => [s.geologicalStandardId, s]),
  );
  const standardsByEquinorCode = new Map(
    geologyStandards.map((s) => [s.equinorCode, s]),
  );

  return archels
    .filter((a) => a.value !== undefined && a.value !== null)
    .map((a) => {
      const id = Number(a.value);
      const displayName =
        a.analogueModelComputeSettingArchelMap?.identifier ||
        a.name ||
        `Archel ${id}`;

      const mapping = a.analogueModelComputeSettingArchelMap;
      const mappedStandard = mapping
        ? (mapping.smdaGeologyStandardId
            ? standardsById.get(mapping.smdaGeologyStandardId)
            : undefined) ??
          (mapping.equinorCode !== undefined
            ? standardsByEquinorCode.get(mapping.equinorCode)
            : undefined)
        : undefined;

      const mappedColorHtml = mappedStandard?.colorHtml;
      const parsedRgb = mappedColorHtml ? hexToRgb(mappedColorHtml) : null;

      if (parsedRgb && mappedColorHtml) {
        return {
          id,
          name: displayName,
          color: mappedColorHtml,
          rgb: parsedRgb,
        };
      }

      const palette = ARCHEL_PALETTE[id] ?? generateColor(id);
      return {
        id,
        name: displayName,
        color: palette.color,
        rgb: palette.rgb,
      };
    })
    .sort((a, b) => a.id - b.id);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface BBox {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export type Proportions = Record<number, number>;

/** Clamp bbox so no edge falls outside the grid's coordinate range. */
export function clampBbox(
  bbox: BBox,
  g: { xMin: number; xMax: number; yMin: number; yMax: number },
): BBox {
  const x0 = Math.max(g.xMin, Math.min(bbox.x0, g.xMax));
  const x1 = Math.max(g.xMin, Math.min(bbox.x1, g.xMax));
  const y0 = Math.max(g.yMin, Math.min(bbox.y0, g.yMax));
  const y1 = Math.max(g.yMin, Math.min(bbox.y1, g.yMax));
  return { x0, x1, y0, y1 };
}

export interface GridData {
  nj: number;
  ni: number;
  nk: number;
  xGrid: Float32Array;
  yGrid: Float32Array;
  archelCounts: Uint32Array;
  layerData: Int32Array;
  xMin: number;
  xMax: number;
  yMin: number;
  yMax: number;
  /** All unique archel IDs present across every layer — precomputed once. */
  presentArchelIds: Set<number>;
}

// ─── Binary parser ────────────────────────────────────────────────────────────

export function parseGridBinary(buffer: ArrayBuffer): GridData {
  const view = new DataView(buffer);
  let offset = 0;

  const magic = String.fromCharCode(
    view.getUint8(0),
    view.getUint8(1),
    view.getUint8(2),
    view.getUint8(3),
  );
  if (magic !== 'ARCH') throw new Error(`Unexpected magic: ${magic}`);
  offset += 4;
  const version = view.getUint32(offset, true);
  offset += 4;
  if (version < 3) throw new Error(`Unsupported binary version: ${version}`);

  const nj = view.getUint32(offset, true);
  offset += 4;
  const ni = view.getUint32(offset, true);
  offset += 4;
  const nArchels = view.getUint32(offset, true);
  offset += 4;
  const nk = view.getUint32(offset, true);
  offset += 4;

  const xGrid = new Float32Array(buffer, offset, nj * ni);
  offset += nj * ni * 4;
  const yGrid = new Float32Array(buffer, offset, nj * ni);
  offset += nj * ni * 4;
  const archelCounts = new Uint32Array(buffer, offset, nArchels * nj * ni);
  offset += nArchels * nj * ni * 4;
  const layerData = new Int32Array(buffer, offset, nk * nj * ni);

  let xMin = Infinity,
    xMax = -Infinity,
    yMin = Infinity,
    yMax = -Infinity;
  for (let k = 0; k < nj * ni; k++) {
    const x = xGrid[k];
    const y = yGrid[k];
    if (!isNaN(x) && !isNaN(y)) {
      if (x < xMin) xMin = x;
      if (x > xMax) xMax = x;
      if (y < yMin) yMin = y;
      if (y > yMax) yMax = y;
    }
  }
  // Collect unique archel IDs once during parse so consumers never have to
  // iterate the full layerData array at render time.
  const presentArchelIds = new Set<number>();
  for (let i = 0; i < layerData.length; i++) {
    presentArchelIds.add(layerData[i]);
  }

  return {
    nj,
    ni,
    nk,
    xGrid,
    yGrid,
    archelCounts,
    layerData,
    xMin,
    xMax,
    yMin,
    yMax,
    presentArchelIds,
  };
}

// ─── Canvas renderer ──────────────────────────────────────────────────────────

export const CANVAS_W = 600;
export const CANVAS_H = 457;
/** Convert a model-space BBox into canvas pixel coordinates. */
export function bboxToCanvas(
  bbox: BBox,
  gridData: GridData,
): { bx0: number; bx1: number; by0: number; by1: number } {
  const { xMin, xMax, yMin, yMax } = gridData;
  const xRange = xMax - xMin;
  const yRange = yMax - yMin;
  return {
    bx0: ((bbox.x0 - xMin) / xRange) * CANVAS_W,
    bx1: ((bbox.x1 - xMin) / xRange) * CANVAS_W,
    by0: CANVAS_H - ((bbox.y1 - yMin) / yRange) * CANVAS_H,
    by1: CANVAS_H - ((bbox.y0 - yMin) / yRange) * CANVAS_H,
  };
}
export interface DragState {
  startCx: number;
  startCy: number;
  currentCx: number;
  currentCy: number;
}

export function renderCanvas(
  canvas: HTMLCanvasElement,
  drag: DragState | null,
  bbox: BBox | null,
  gridData: GridData,
  selectedLayer: number | null,
  archels: ArchelMeta[],
  interactive = false,
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const { nj, ni, layerData } = gridData;
  const layer = selectedLayer ?? 0;
  const sourceGrid = layerData.subarray(layer * nj * ni, (layer + 1) * nj * ni);

  // Build a fast id→rgb lookup so rendering doesn't search the array per pixel
  const rgbById: Record<number, [number, number, number]> = {};
  for (const a of archels) rgbById[a.id] = a.rgb;
  const fallbackRgb: [number, number, number] = [158, 158, 158];

  const imgData = ctx.createImageData(CANVAS_W, CANVAS_H);
  for (let cy = 0; cy < CANVAS_H; cy++) {
    const gi = Math.min(
      ni - 1,
      Math.floor(((CANVAS_H - 1 - cy) / CANVAS_H) * ni),
    );
    for (let cx = 0; cx < CANVAS_W; cx++) {
      const gj = Math.min(nj - 1, Math.floor((cx / CANVAS_W) * nj));
      const archelId = sourceGrid[gj * ni + gi];
      const [r, g, b] = rgbById[archelId] ?? fallbackRgb;
      const pi = (cy * CANVAS_W + cx) * 4;
      imgData.data[pi] = r;
      imgData.data[pi + 1] = g;
      imgData.data[pi + 2] = b;
      imgData.data[pi + 3] = 255;
    }
  }
  ctx.putImageData(imgData, 0, 0);

  if (bbox) {
    const { bx0, bx1, by0, by1 } = bboxToCanvas(bbox, gridData);

    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(0, 0, CANVAS_W, by0);
    ctx.fillRect(0, by1, CANVAS_W, CANVAS_H - by1);
    ctx.fillRect(0, by0, bx0, by1 - by0);
    ctx.fillRect(bx1, by0, CANVAS_W - bx1, by1 - by0);

    // Guide lines from bbox edges to canvas borders (axis alignment)
    ctx.save();
    ctx.strokeStyle = 'rgba(255, 215, 0, 0.6)';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    // Left edge → left border
    ctx.beginPath();
    ctx.moveTo(bx0, by0);
    ctx.lineTo(0, by0);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(bx0, by1);
    ctx.lineTo(0, by1);
    ctx.stroke();
    // Bottom edge → bottom border
    ctx.beginPath();
    ctx.moveTo(bx0, by1);
    ctx.lineTo(bx0, CANVAS_H);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(bx1, by1);
    ctx.lineTo(bx1, CANVAS_H);
    ctx.stroke();
    ctx.restore();

    // Bbox outline
    ctx.save();
    ctx.strokeStyle = '#FFD700';
    ctx.lineWidth = 2;
    ctx.shadowColor = 'rgba(0,0,0,0.6)';
    ctx.shadowBlur = 6;
    ctx.strokeRect(bx0, by0, bx1 - bx0, by1 - by0);
    ctx.restore();

    if (interactive) {
      const mx = (bx0 + bx1) / 2;
      const my = (by0 + by1) / 2;
      const handles: [number, number][] = [
        [bx0, by0],
        [mx, by0],
        [bx1, by0],
        [bx1, my],
        [bx1, by1],
        [mx, by1],
        [bx0, by1],
        [bx0, my],
      ];
      ctx.save();
      ctx.fillStyle = '#FFD700';
      ctx.strokeStyle = 'rgba(0,0,0,0.7)';
      ctx.lineWidth = 1;
      for (const [hx, hy] of handles) {
        ctx.fillRect(hx - 4, hy - 4, 8, 8);
        ctx.strokeRect(hx - 4, hy - 4, 8, 8);
      }
      ctx.restore();
    }
  }

  if (drag) {
    const x = Math.min(drag.startCx, drag.currentCx);
    const y = Math.min(drag.startCy, drag.currentCy);
    const w = Math.abs(drag.currentCx - drag.startCx);
    const h = Math.abs(drag.currentCy - drag.startCy);
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.strokeRect(x, y, w, h);
    ctx.restore();
  }
}
